// redux/Slices/boardContextSlice.js
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { config } from '../../config';

// Async thunk to fetch user's board context
export const fetchBoardContext = createAsyncThunk(
    'boardContext/fetchBoardContext',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/users/me/board-context`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch board context');
            }

            const data = await response.json();
            return data.boardContext || [];
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk to update user's board context
export const updateBoardContext = createAsyncThunk(
    'boardContext/updateBoardContext',
    async (selectedBoards, { rejectWithValue }) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/users/me/board-context`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ boardContext: selectedBoards })
            });

            if (!response.ok) {
                throw new Error('Failed to update board context');
            }

            const data = await response.json();
            return data.boardContext || [];
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk to fetch available boards with search
export const fetchAvailableBoards = createAsyncThunk(
    'boardContext/fetchAvailableBoards',
    async ({ search = '', limit = 20 }, { rejectWithValue }) => {
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const response = await fetch(`${config.API_URI}/v1/users/me/boards?limit=${limit}${searchParam}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch boards');
            }

            const data = await response.json();
            return data.boards || [];
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const boardContextSlice = createSlice({
    name: 'boardContext',
    initialState: {
        selectedBoards: [], // User's manually selected board context
        availableBoards: [], // All available boards for selection
        currentBoardId: null, // Current board shortId when on board page
        loading: false,
        availableBoardsLoading: false,
        error: null,
        searchQuery: '',
    },
    reducers: {
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        setCurrentBoardId: (state, action) => {
            state.currentBoardId = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        // Optimistic update for better UX
        toggleBoardSelection: (state, action) => {
            const board = action.payload;
            const isSelected = state.selectedBoards.some(b => b.shortId === board.shortId);

            if (isSelected) {
                state.selectedBoards = state.selectedBoards.filter(b => b.shortId !== board.shortId);
            } else {
                state.selectedBoards.push(board);
            }
        },
        clearSelection: (state) => {
            state.selectedBoards = [];
        },

        // ========== OPTIMISTIC ACTIONS FOR REAL-TIME UPDATES ==========

        // Update board properties in availableBoards and selectedBoards
        optimisticUpdateBoardInContext: (state, action) => {
            const { boardId, updates } = action.payload;

            // Helper function to update board in an array
            const updateBoardInArray = (boardsArray) => {
                const index = boardsArray.findIndex(
                    board => board._id === boardId || board.shortId === boardId
                );
                if (index !== -1) {
                    boardsArray[index] = {
                        ...boardsArray[index],
                        ...updates
                    };
                }
            };

            // Update in both arrays
            updateBoardInArray(state.availableBoards);
            updateBoardInArray(state.selectedBoards);
        },

        // Add new board to availableBoards
        optimisticAddBoardToContext: (state, action) => {
            const board = action.payload;

            // Check if board already exists (avoid duplicates)
            const exists = state.availableBoards.some(
                b => b._id === board._id || b.shortId === board.shortId
            );

            if (!exists) {
                // Add to beginning of availableBoards array (most recent)
                state.availableBoards.unshift(board);
            }
        },

        // Remove board from availableBoards and selectedBoards
        optimisticRemoveBoardFromContext: (state, action) => {
            const boardId = action.payload;

            // Remove from availableBoards
            state.availableBoards = state.availableBoards.filter(
                board => board._id !== boardId && board.shortId !== boardId
            );

            // Remove from selectedBoards
            state.selectedBoards = state.selectedBoards.filter(
                board => board._id !== boardId && board.shortId !== boardId
            );
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Board Context
            .addCase(fetchBoardContext.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBoardContext.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedBoards = action.payload;
            })
            .addCase(fetchBoardContext.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update Board Context
            .addCase(updateBoardContext.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateBoardContext.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedBoards = action.payload;
            })
            .addCase(updateBoardContext.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Available Boards
            .addCase(fetchAvailableBoards.pending, (state) => {
                state.availableBoardsLoading = true;
                state.error = null;
            })
            .addCase(fetchAvailableBoards.fulfilled, (state, action) => {
                state.availableBoardsLoading = false;
                state.availableBoards = action.payload;
            })
            .addCase(fetchAvailableBoards.rejected, (state, action) => {
                state.availableBoardsLoading = false;
                state.error = action.payload;
            });
    }
});

export const {
    setSearchQuery,
    setCurrentBoardId,
    clearError,
    toggleBoardSelection,
    clearSelection,
    optimisticUpdateBoardInContext,
    optimisticAddBoardToContext,
    optimisticRemoveBoardFromContext
} = boardContextSlice.actions;

// Selector to get effective board context based on the logic:
// 1. If user manually selects board(s) -> always use selected boards (regardless of current page)
// 2. If no boards selected:
//    - If on board page -> use current board
//    - If not on board page -> use all user boards
const selectCurrentBoardId = (state) => state.boardContext.currentBoardId;
const selectSelectedBoards = (state) => state.boardContext.selectedBoards;
const selectAvailableBoards = (state) => state.boardContext.availableBoards;

export const selectEffectiveBoardContext = createSelector(
  [selectSelectedBoards, selectCurrentBoardId, selectAvailableBoards],
  (selectedBoards, currentBoardId, availableBoards) => {
    // Case 1: User has manually selected specific boards - always use these (highest priority)
    if (selectedBoards.length > 0) {
      return {
        type: 'selected_boards',
        boards: selectedBoards,
        boardIds: selectedBoards.map((board) => board.shortId),
      };
    }

    // Case 2: No boards selected + on board page - use current board
    if (currentBoardId) {
      const currentBoard = availableBoards.find(
        (board) => board.shortId === currentBoardId
      );
      return {
        type: 'current_board',
        boards: currentBoard ? [currentBoard] : [],
        boardIds: currentBoard ? [currentBoard.shortId] : [],
      };
    }

    // Case 3: No boards selected + not on board page - use all available boards (limited to 3 for performance)
    const limitedBoards = availableBoards.slice(0, 3);
    return {
      type: 'all_boards',
      boards: limitedBoards,
      boardIds: limitedBoards.map((board) => board.shortId),
    };
  }
);

export default boardContextSlice.reducer;
